import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const reviewVideoSkill: Skill = {
    name: SKILL_NAMES.reviewVideo,
    description:
        'The video review procedure. Load BEFORE starting any review — the review phase of every build, or when asked to review or polish a video.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Reviewing a Video

The review is pass/fail against a fixed defect list — not a subjective score — so
it terminates. Finding a problem obligates you to fix it and re-check, never to
report it and move on.

## Procedure
1. **Render everything**: \`${TOOL_NAMES.imageAsk}\` on the scenes in parallel, checking each against the critical-defect list below.
2. **Fix all failures in one batch** (fixes on different scenes are independent).
3. **Re-render only the scenes you changed and re-check.** Repeat until clean.
4. **Lint**

## Critical-defect list (the only things that fail a scene)
- text overflowing its asset, clipped, or too small to read
- an empty or half-built asset (e.g. "Click to enter your text")
- cheesy/clichéd media, or media that clearly doesn't match the point
- a layout that's clearly broken (elements colliding, text off-frame)
- a drawn object or icon you can't name without being told what it is (motion-graphics scenes draw their own symbols, and the common failure is a shape that reads as nothing — a ring with marks in it, a squiggle in a circle)
- on a text scene (no narration): text that doesn't communicate a complete thought on its own
- any text on screen or in the transcript that wasn't meant to be user-facing (such as placeholder, thinking note, or instruction)

NOT defects:
- **watermarks on stock media** (stock renders watermarked in the editor and is licensed/watermark removed
at render time — never fail a scene or swap media over one).
- on a voiceover/talking scene (with narration): text that doesn't communicate a complete thought on its own

Anything not on this list — "could be a bit more dynamic" polish — is not a reason
to fail and not something to loop on. Fix defects, not taste.

**Swap the design instead of degrading the copy.** If making a scene's text fit
has taken more than ~2 rewrites, or the words only fit as disjointed fragments,
the design is wrong for that content. Pick a different, more suitable scene design and
delete/recreate the scene.

**Attempt cap + escape hatch:** give a failing scene at most **3 fix rounds**.
If a defect still remains after 3 rounds accept the scene with that residual noted and move on.
If you're still making progress you can go past 3.
The cap stops oscillating on the same unfixable thing, not fixing real defects.

## What to ask when rendering a scene
Ask \`${TOOL_NAMES.imageAsk}\` explicitly:
- "Does this scene look awkward, unbalanced, or off in any way?"
- Scenes with drawn symbols: "Name each drawn object in this scene without being
  told what it's meant to be." Anything you can only describe as a shape ("a
  circle with lines in it") is a defect, however tidy the scene looks — asking
  whether the scene is *balanced* will not surface it.
- "Does this media obviously match what the scene is about?"
- "Does the media look cheesy, clichéd, or like generic stock (staged office,
  thumbs-up, lightbulb/gears/rocket metaphor, dated look, or baked-in VFX like
  particles/light streaks/hologram graphics)?"
- Text scenes (no narration): "Reading this scene's text top to bottom, does it
  communicate a complete thought on its own?" Headings are fine, but a scene that
  reads as disjointed fragments fails.
- Shape-bounded text (circle/badge/pill): confirm the text sits cleanly inside.
- Voiceover: review each overlay separately, cross-check its text word-by-word
  against its covered words, and confirm split points read naturally.

## Lint pass
Run \`${TOOL_NAMES.lintVideo}\` and fix findings. Then verify what lint can't by inspecting the video state:
1. **Narration present?** A voiceover/talking-head/recorded group exists — unless
   the user explicitly opted out.
2. **Every scene built?** No empty or half-built scenes.
3. **Lists and numbers hold up?** Counts match, no dropped items, referenced
   numbers visible.
4. **Text legible and self-standing?** No tiny type, no cryptic fragments
5. **Does it look good?** Opens on an effective intro/title scene; list scenes use list designs;
   quote designs hold real quotations with the speaker credited in the name/role
   slots, not ordinary copy with those slots left empty; media fits and looks authentic.
6. **A complete story?** Read the whole video as a viewer — narration end to end
   plus on-screen text and media in order. It opens with a real hook, flows, and
   closes with a real outro rather than stopping mid-thought.

If any check fails, the video isn't done. Keep fixing and re-verifying until every
check passes and it's clean and render-ready.`;
    },
};
